import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, MessageSquareQuote, Sparkles, ArrowRight, Trophy, Repeat, Mic, Gauge, Pause, MessageCircleWarning, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import SessionRecordingsPanel from "../components/SessionRecordingsPanel";

const VoiceAnalyticsCard = ({ stats, voiceFeedback }) => {
  const pacingColor =
    stats.pacing === "ideal" ? "text-novatus-green" :
    stats.pacing === "ruhig" || stats.pacing === "schnell" ? "text-amber-400" :
    "text-red-400";
  const tips = Array.isArray(voiceFeedback) ? voiceFeedback : (typeof voiceFeedback === "string" ? [voiceFeedback] : []);

  return (
    <div className="nv-card mt-5 p-6 sm:p-8 animate-slide-up delay-3" data-testid="voice-analytics-card">
      <div className="flex items-center gap-2 mb-5">
        <Mic className="w-5 h-5 text-novatus-green" />
        <h3 className="font-display font-bold text-lg text-white">Voice-Analytics</h3>
        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40 ml-2">{stats.voice_turns} Voice-Turns</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4 text-center">
          <Gauge className="w-4 h-4 mx-auto mb-2 text-novatus-green" />
          <div className={`font-display font-black text-2xl ${pacingColor}`}>{stats.avg_wpm}</div>
          <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-1">Wörter/Min</div>
        </div>
        <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4 text-center">
          <div className={`font-display font-black text-base capitalize mt-1 ${pacingColor}`}>{stats.pacing}</div>
          <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-2">Sprechtempo</div>
          <div className="text-[10px] text-novatus-text-3 mt-1">ideal: 130–160</div>
        </div>
        <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4 text-center">
          <Pause className="w-4 h-4 mx-auto mb-2 text-novatus-text-2" />
          <div className="font-display font-black text-2xl text-white">{stats.total_pauses}</div>
          <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-1">Pausen ({stats.long_pauses} lang)</div>
        </div>
        <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4 text-center">
          <MessageCircleWarning className={`w-4 h-4 mx-auto mb-2 ${stats.filler_ratio_pct > 5 ? "text-amber-400" : "text-novatus-text-2"}`} />
          <div className={`font-display font-black text-2xl ${stats.filler_ratio_pct > 5 ? "text-amber-400" : "text-white"}`}>{stats.total_fillers}</div>
          <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-1">Füllwörter ({stats.filler_ratio_pct}%)</div>
        </div>
        <div className="bg-novatus-card-2 border border-novatus rounded-lg p-4 text-center">
          <div className="font-display font-black text-2xl text-white">{stats.clarity_pct ?? "—"}{stats.clarity_pct != null ? "%" : ""}</div>
          <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-2">Klarheit</div>
          <div className="text-[10px] text-novatus-text-3 mt-1">Aussprache-Score</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <div className="text-xs text-novatus-text-3">
          <span className="text-novatus-text-2">Gesprochen:</span> {stats.total_speaking_s}s · {stats.total_words} Wörter
        </div>
      </div>

      {tips.length > 0 && (
        <div className="mt-5 pt-5 border-t border-novatus">
          <p className="label-eyebrow mb-3 text-novatus-green">KI-Coaching für deine Stimme</p>
          <ul className="space-y-2">
            {tips.map((tip, i) => (
              <li key={`tip-${i}-${(tip || "").slice(0, 24)}`} className="flex gap-3 text-sm text-white leading-relaxed" data-testid={`voice-tip-${i}`}>
                <Sparkles className="w-3.5 h-3.5 text-novatus-green mt-1 shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const BigRing = ({ score = 0 }) => {  const size = 220;
  const radius = (size - 24) / 2;
  const c = 2 * Math.PI * radius;
  const offset = c - (score / 100) * c;
  const color = score >= 70 ? "#1FCB4F" : score >= 50 ? "#FBBF24" : "#EF4444";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="#262626" strokeWidth="14" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} stroke={color} strokeWidth="14" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.4s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-black text-6xl text-white">{score}</span>
        <span className="text-xs uppercase tracking-[0.2em] text-novatus-text-3 font-semibold mt-1">von 100</span>
      </div>
    </div>
  );
};

const Feedback = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/training/sessions/${sessionId}`);
        setData(data);
      } catch (e) { console.warn("Feedback load failed", e); }
      finally { setLoading(false); }
    })();
  }, [sessionId]);

  const share = async () => {
    setSharing(true);
    try {
      const { data: rec } = await api.post("/admin/recordings/share", { session_id: sessionId, is_public: true });
      toast.success("Mitschnitt geteilt", { description: "KI hat Highlights generiert." });
      navigate(`/highlights/${rec.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Teilen fehlgeschlagen";
      toast.error(detail);
      setSharing(false);
    }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-novatus-green" /></div>;

  const fb = data?.feedback?.data;
  const session = data?.session;

  if (!fb) return (
    <div className="max-w-3xl mx-auto p-12 text-center">
      <p className="text-novatus-text-3">Auswertung wird vorbereitet ...</p>
      <button onClick={() => navigate("/training")} className="mt-6 btn-green h-11 px-5 rounded-lg">Zurück</button>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="animate-slide-up text-center">
        <span className="label-eyebrow">Auswertung</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
          Training <span className="text-novatus-green">ausgewertet.</span>
        </h1>
        <p className="text-novatus-text-2 mt-3 max-w-xl mx-auto">Szenario: <span className="font-semibold text-white">{session?.scenario_title}</span></p>
      </div>

      <div className="nv-card mt-8 p-7 sm:p-10 flex flex-col md:flex-row items-center gap-10 animate-slide-up delay-1" data-testid="feedback-hero">
        <BigRing score={fb.score} />
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start">
            <Trophy className="w-5 h-5 text-novatus-green" />
            <span className="label-eyebrow">XP verdient</span>
          </div>
          <p className="font-display font-black text-5xl text-novatus-green mt-2">+{data?.session?.xp_earned ?? 0} XP</p>
          <p className="text-novatus-text-2 mt-4 leading-relaxed">{fb.summary}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            {Object.entries(fb.category_scores || {}).slice(0, 4).map(([k, v]) => (
              <div key={k} className="bg-novatus-card-2 rounded-lg p-3 text-center border border-novatus">
                <div className="font-display font-black text-lg text-white">{v}</div>
                <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-0.5 leading-tight">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        <div className="nv-card p-6 animate-slide-up delay-2" data-testid="strengths-card" style={{ borderColor: "rgba(31, 203, 79, 0.3)" }}>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-lg text-white">3 Stärken</h3>
          </div>
          <ul className="space-y-3">
            {(fb.strengths || []).map((s, i) => (
              <li key={`str-${i}-${(s || "").slice(0, 24)}`} className="flex gap-3" data-testid={`strength-${i}`}>
                <span className="font-display font-black text-novatus-green text-lg leading-none">0{i+1}</span>
                <span className="text-sm text-white leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="nv-card p-6 animate-slide-up delay-2" data-testid="improvements-card" style={{ borderColor: "rgba(251, 191, 36, 0.3)" }}>
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-amber-400" />
            <h3 className="font-display font-bold text-lg text-white">3 Verbesserungen</h3>
          </div>
          <ul className="space-y-3">
            {(fb.improvements || []).map((s, i) => (
              <li key={`imp-${i}-${(s || "").slice(0, 24)}`} className="flex gap-3" data-testid={`improvement-${i}`}>
                <span className="font-display font-black text-amber-400 text-lg leading-none">0{i+1}</span>
                <span className="text-sm text-white leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Coaching comment from trainer (if present) */}
      {session?.coaching?.text && (
        <div className="nv-card mt-5 p-6 sm:p-8 animate-slide-up delay-2 relative overflow-hidden" data-testid="coaching-feedback-card" style={{
          borderColor: "rgba(31, 203, 79, 0.4)",
          background: "linear-gradient(135deg, rgba(31,203,79,0.06), transparent 70%)",
        }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-novatus-green/15 border border-novatus-green/40 flex items-center justify-center shrink-0">
              <MessageSquareQuote className="w-5 h-5 text-novatus-green" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="label-eyebrow text-novatus-green">Hinweis von deinem Trainer</span>
                <span className="text-xs text-novatus-text-3">·</span>
                <span className="text-xs text-novatus-text-2">{session.coaching.by_name}</span>
              </div>
              <p className="text-base sm:text-lg text-white mt-3 leading-relaxed whitespace-pre-wrap">{session.coaching.text}</p>
              <p className="text-[10px] text-novatus-text-3 mt-3 uppercase tracking-wider">
                {new Date(session.coaching.updated_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Voice Analytics (only if voice was used) */}
      {data?.feedback?.voice_stats && (
        <VoiceAnalyticsCard stats={data.feedback.voice_stats} voiceFeedback={fb.voice_feedback} />
      )}

      {/* Live-Coaching log from the call (if any hints were emitted) */}
      {session?.coaching_hints?.length > 0 && (
        <div className="nv-card mt-5 p-6 sm:p-8 animate-slide-up delay-3" data-testid="coaching-log-card">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-lg text-white">Live-Tipps im Anruf</h3>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40 ml-2">
              {session.coaching_hints.length} Hinweise
            </span>
          </div>
          <p className="text-xs text-novatus-text-3 mb-4 leading-relaxed">
            Was der KI-Coach dir während des Anrufs zugerufen hat — sortiert nach Reihenfolge.
          </p>
          <div className="space-y-2">
            {session.coaching_hints.map((h, i) => {
              const isWarn = h.severity === "warn";
              const isContent = h.kind === "content";
              return (
                <div
                  key={`hint-${i}-${h.created_at}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    isWarn
                      ? "bg-amber-500/8 border-amber-400/30"
                      : "bg-novatus-card-2 border-novatus"
                  }`}
                  data-testid={`coaching-log-item-${i}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    isWarn ? "bg-amber-500/20" : "bg-novatus-green/15"
                  }`}>
                    <Sparkles className={`w-3.5 h-3.5 ${isWarn ? "text-amber-300" : "text-novatus-green"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] uppercase tracking-wider font-bold ${
                        isContent ? "text-novatus-green" : "text-novatus-text-2"
                      }`}>
                        {isContent ? "Trainer-Tipp" : "Voice"}
                      </span>
                      {h.created_at && (
                        <span className="text-[10px] text-novatus-text-3">
                          {new Date(h.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white leading-snug">{h.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-5 animate-slide-up delay-3">
        <SessionRecordingsPanel sessionId={sessionId} eyebrow="Höre deinen Anruf nochmal an" />
      </div>

      {fb.better_phrasings?.length > 0 && (        <div className="nv-card mt-5 p-6 sm:p-8 animate-slide-up delay-3" data-testid="better-phrasings-card">
          <div className="flex items-center gap-2 mb-5">
            <MessageSquareQuote className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-lg text-white">Bessere Formulierungen</h3>
          </div>
          <div className="space-y-3">
            {fb.better_phrasings.map((p, i) => (
              <div key={`phr-${i}-${(p.original || "").slice(0, 24)}`} className="grid md:grid-cols-2 gap-3 p-4 rounded-lg bg-novatus-card-2 border border-novatus" data-testid={`phrasing-${i}`}>
                <div>
                  <p className="label-eyebrow text-red-400/80">Original</p>
                  <p className="text-sm text-novatus-text-2 mt-1.5 leading-relaxed line-through opacity-80">"{p.original}"</p>
                </div>
                <div>
                  <p className="label-eyebrow text-novatus-green">Empfehlung</p>
                  <p className="text-sm text-white mt-1.5 leading-relaxed font-medium">"{p.improved}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="nv-card mt-5 p-6 sm:p-8 animate-slide-up delay-4 relative overflow-hidden" data-testid="next-recommendation-card">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: 'radial-gradient(circle at 90% 50%, rgba(31,203,79,0.3) 0%, transparent 60%)',
        }} />
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-novatus-green" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-novatus-green">Nächste Empfehlung</span>
            </div>
            <h3 className="font-display font-bold text-2xl text-white">{fb.recommended_next_training || "Weiter trainieren"}</h3>
            <p className="text-novatus-text-2 mt-2 text-sm">Basierend auf deiner Performance schlägt die KI dieses Szenario als Nächstes vor.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {isAdmin && (
              <button onClick={share} disabled={sharing} className="btn-ghost-green h-11 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="share-recording-btn">
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Als Lern-Mitschnitt teilen
              </button>
            )}
            <button onClick={() => navigate("/training")} className="btn-ghost-green h-11 px-4 rounded-lg flex items-center gap-2" data-testid="goto-trainings-btn">
              <Repeat className="w-4 h-4" /> Mehr Trainings
            </button>
            <button onClick={() => navigate("/dashboard")} className="btn-green h-11 px-5 rounded-lg flex items-center gap-2" data-testid="goto-dashboard-btn">
              Dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
